import React, { useState, useEffect } from "react";
import { Modal, Button, FormSelect, FormInput } from "../../../components/ui";
import api from "../../../services/api";
import toast from "react-hot-toast";
import { Plus, Trash2, Package } from "lucide-react";
import { getCustomerDisplayId } from "../../../constants/globalConstants";

const TRADES = [
  "modular", "carpentry", "civil", "electrical", "plumbing", "painting", "false_ceiling", "fabrication", "general"
];

export const RequestMaterialsModal = ({
  isOpen,
  onClose,
  project,
  projects = [],
  initialItems = {}, // { itemId: qty }
  initialDepartment = "",
  requestId = null,
  viewerRole,
  source = "inventory",
  inventory = [],
  onSuccess
}) => {
  const [selectedProject, setSelectedProject] = useState("");
  const [trade, setTrade] = useState(initialDepartment || "modular");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setSelectedProject(project?._id || project?.id || "");
      setTrade(initialDepartment || "modular");
      
      const convertedItems = Object.keys(initialItems).map(id => ({
        itemId: id,
        qty: initialItems[id] || 1
      }));
      
      setItems(convertedItems.length > 0 ? convertedItems : [{ itemId: "", qty: 1 }]);
    }
  }, [isOpen, project, initialItems, initialDepartment]);

  const handleAddItem = () => {
    setItems([...items, { itemId: "", qty: 1 }]);
  };

  const handleRemoveItem = (idx) => {
    setItems(items.filter((_, i) => i !== idx));
  };

  const handleItemChange = (idx, field, value) => {
    const newItems = [...items];
    newItems[idx][field] = value;
    setItems(newItems);
  };

  const handleSubmit = async () => {
    if (!selectedProject) return toast.error("Please select a project.");
    
    const validItems = items.filter(it => it.itemId && Number(it.qty) > 0);
    if (validItems.length === 0) return toast.error("Please add at least one valid item to request.");

    const payload = {
      projectId: selectedProject,
      installationTrade: trade,
      items: validItems.map(it => ({
        itemId: it.itemId,
        quantity: Number(it.qty)
      })),
      source
    };

    try {
      setLoading(true);
      if (requestId) {
        // Edit or append to existing request
        await api.patch(`/inventory/material-requests/${requestId}`, payload);
        toast.success("Request updated successfully.");
      } else {
        // Create new request
        await api.post(`/inventory/material-requests`, payload);
        toast.success("Material request submitted.");
      }
      onSuccess?.();
    } catch (err) {
      console.error("Material request error:", err);
      toast.error(err.response?.data?.message || "Failed to submit request.");
    } finally {
      setLoading(false);
    }
  };

  // Prepare inventory options
  const inventoryOptions = inventory.map(item => ({
    value: item._id || item.id,
    label: `${item.name || item.itemName} (${item.availableQty} ${item.unit || 'units'} available)`
  }));

  const projectOptions = projects.map(p => ({
    value: p._id || p.id,
    label: `${p.customerName || p.name} - ${getCustomerDisplayId(p)}`
  }));

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={requestId ? "Update Material Request" : "New Material Request"}
      size="max-w-3xl"
    >
      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <FormSelect
            label="Project *"
            options={[{ value: "", label: "Select Project..." }, ...projectOptions]}
            value={selectedProject}
            onChange={(e) => setSelectedProject(e.target.value)}
            disabled={!!project || !!requestId}
          />
          <FormSelect
            label="Department / Trade *"
            options={TRADES.map(t => ({ value: t, label: t.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase()) }))}
            value={trade}
            onChange={(e) => setTrade(e.target.value)}
          />
        </div>

        <div className="space-y-4">
          <div className="flex items-center justify-between border-b border-slate-100 pb-2">
            <h3 className="text-sm font-semibold text-brand-dark flex items-center gap-2">
              <Package size={16} className="text-brand-orange" />
              Requested Materials
            </h3>
            <Button variant="secondary" onClick={handleAddItem} size="sm" className="text-xs">
              <Plus size={14} className="mr-1" /> Add Item
            </Button>
          </div>

          <div className="space-y-3 max-h-[40vh] overflow-y-auto pr-2">
            {items.map((it, idx) => (
              <div key={idx} className="flex items-start gap-3 bg-slate-50 p-3 rounded-xl border border-slate-100">
                <div className="flex-1">
                  <FormSelect
                    options={[{ value: "", label: "Search and select item..." }, ...inventoryOptions]}
                    value={it.itemId}
                    onChange={(e) => handleItemChange(idx, "itemId", e.target.value)}
                  />
                </div>
                <div className="w-24">
                  <FormInput
                    type="number"
                    min="1"
                    value={it.qty}
                    onChange={(e) => handleItemChange(idx, "qty", e.target.value)}
                    placeholder="Qty"
                  />
                </div>
                <button
                  onClick={() => handleRemoveItem(idx)}
                  className="mt-1 w-9 h-9 flex items-center justify-center text-red-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors flex-shrink-0"
                  title="Remove Item"
                >
                  <Trash2 size={16} />
                </button>
              </div>
            ))}
            {items.length === 0 && (
              <div className="text-center py-6 text-sm text-slate-400 italic">
                No items added to this request yet.
              </div>
            )}
          </div>
        </div>

        <div className="flex items-center justify-end gap-3 pt-4 border-t border-slate-100">
          <Button variant="secondary" onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleSubmit} isLoading={loading}>
            {requestId ? "Update Request" : "Submit Request"}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

